import React from 'react';
import { useDispatch } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { ElectionContainer } from '../election/ElectionContainer';
import { Translator, getTranslator } from '../../Translator';
import { NotFound } from '../NotFound';
import { CandidateState, ElectionStatus, Routes } from '../../_generatedTypes.g';
import { DocumentTitle } from '../DocumentTitle';
import { QuestionMark } from '../ui/QuestionMark';
import { FilterList } from '../ui/FilterList';
import { getPartyById, canEditPartyText, canEditPartyStructure, getElectionById } from '../../selectors';
import { SelectPartyAdminScreen } from '../selectPartyAdmin/SelectPartyAdminScreen';
import { Toggle } from '../ui/Toggle';
import { addBlankCandidateAndSetEditMode, removeInlineCandidateIfEmpty } from '../../features/candidate/candidatesSlice';
import { deletePartyAdmin, selectPaScreenShown } from '../../features/partyadmin/PartyAdminScreenSlice';
import { CandidateItem } from '../candidate/CandidateItem';
import { candidateSearched } from '../../features/search/searchSlice';
import { addModeSet } from '../../features/party/PartyScreenSlice';
import { useAppSelector } from '../../hooks';

type PartyComponentParams = {
    partyId: string;
}
export const PartyComponent = () => {
    const language = useAppSelector(state => state.language);
    const dispatch = useDispatch();
    const token = useAppSelector(state => state.clientState.RequestVerificationToken);
    const searchTermCandidate = useAppSelector(state => state.searchItemState.SearchTermCandidate);
    const elections = useAppSelector(state => state.elections);
    const account = useAppSelector(state => state.clientState.Account);
    const partyScreenState = useAppSelector(state => state.partyScreen);
    const parties = useAppSelector(state => state.parties);
    const candidates = useAppSelector(state => state.candidates);
    const partyRoute = useParams<PartyComponentParams>();

    const partyId = Number(partyRoute.partyId);

    const party = getPartyById(parties, partyId);

    if (!party) {
        return <NotFound />
    }

    const candidatesForParty = candidates.filter(c => c.PartyId === partyId);
    const canEditText = canEditPartyText(account, parties, elections, party.PartyId);
    const canEditStructure = canEditPartyStructure(account, parties, elections, party.PartyId);
    const addMode = partyScreenState.addMode;
    const t = getTranslator(language);
    const election = getElectionById(elections, party.ElectionId);
    const canShowCandidacyApproved = (account.IsAdmin || party.IsCurrentAccountPartyAdmin && election?.ElectionStatus == ElectionStatus.CandidacyPeriod);

    const onkeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Escape') {
            removeInlineCandidateIfEmpty(partyScreenState.inlineEditingCandidateId, candidatesForParty, dispatch);
            addModeSet({ addMode: false });
        }
    };
    return <ElectionContainer partyId={party.PartyId}>
        <div>
            <QuestionMark>
                <div className="popup-help">
                    {canEditText ?
                        <>
                            {election.ElectionStatus == ElectionStatus.CandidacyPeriod ?
                                <>
                                    <h2>{t.translate({ NL: 'Kandidaatstelling/verkiezing vullen:', EN: 'Fill in your candidacy/election form:' })}</h2>
                                    <ul>
                                        <li>{t.translate({ NL: 'Klik op ‘bewerken’ om partij-informatie in te voeren.', EN: 'Click ‘edit’ to enter party information.' })}</li>
                                        <li>{t.translate({ NL: 'Klik op ‘Kandidaten toevoegen’ om kandidaten aan de partij toe te voegen.', EN: 'Click on ‘Add candidates’ to add candidates to the party' })}</li>
                                        <li>{t.translate({ NL: 'Klik na het invullen van de naam en gebruikersnaam op het opslaan teken of klik enter.', EN: 'After entering the name and username, click on the save sign or click enter.' })}</li>
                                        <li>{t.translate({ NL: 'Vul alle kandidaten op de lijst in en kies dan ‘klaar met toevoegen’.', EN: 'Enter all candidates on the list and then choose ‘finished adding’.' })}</li>
                                        <li>{t.translate({ NL: 'Klik nu op je eigen naam om verkiezingsteksten in te voeren en om je kandidaatstelling goed te keuren .', EN: 'Now click on your own name to enter election texts and to approve your candidacy.' })}</li>
                                        <li>{t.translate({ NL: 'Zorg dat alle kandidaten van jouw partij hun kandidaatstelling op tijd goedkeuren.', EN: 'Make sure that all candidates from your party approve their candidacy on time.' })}</li>
                                    </ul>
                                </>
                                :
                                <>
                                    <h2>{t.translate({ NL: 'Stemmen:', EN: 'Vote:' })}</h2>
                                    <p>{t.translate({ NL: 'Klik op de kandidaat waarvoor je je stem wilt uitbrengen of zoek de kandidaat door een deel van de naam in het zoekveld te typen.', EN: 'Click on the candidate you want to vote for or search for the candidate by typing part of the name in the search field.' })}</p>

                                    <h2>{t['Editing your party’s information']}</h2>
                                    <p>
                                        {t['If the institution authorized you to edit your party’s details, this screen includes a button ‘Edit’. This option is only available ']}
                                        <b>{t['before the election opens']}</b>
                                        {t['. Clicking the button will allow you to edit the party’s descriptions (in Dutch and English) and enter the url of the party’s website.']}
                                    </p>
                                    <p>
                                        {t['When you are done editing, click ‘Save’ to confirm your changes.']}<br />
                                    </p>
                                </>}
                        </>
                        :
                        <>
                            <h2>{t.translate({ NL: 'Stemmen:', EN: 'Vote:' })}</h2>
                            <p>{t.translate({ NL: 'Klik op de kandidaat waar je informatie over wilt zien of waar je op wilt stemmen.', EN: 'Click on the candidate you want to see information about or vote for.' })}</p>
                        </>
                    }
                </div>
            </QuestionMark>
            <h1>{t.translate(party.Name)}</h1>
            <DocumentTitle title={t.translate(party.Name)} />
            {party.HasURL
                ? <p><a href={Routes.Parties.Visit(party.PartyId)} target="_blank" rel="noreferrer noopener">{t['Visit campaign page']}</a></p>
                : null}
            <div>
                <p className="party__description">{t.translate({ NL: party.Description_NL, EN: party.Description_EN })}</p>
            </div>
            {(account.IsAdmin && canEditText) && <>
                <h2>{t['Party admins']}</h2>
                <table><tbody>
                    {party.Administrators.map(admin => <tr key={admin}><td key={0}>{admin}</td><td key={1}><a onClick={() => deletePartyAdmin(admin, party.PartyId, token, dispatch)} style={{ textDecoration: 'underline', cursor: 'pointer' }}>{t['Revoke']}</a></td></tr>)}
                </tbody></table>
                <p>
                    <a style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => dispatch(selectPaScreenShown({ visible: true }))}>{t['Add administrator to party']}</a>
                </p>
            </>}
            {account.IsAdmin && <table className="election__details"><tbody>
                <tr>
                    <th>{t['Party code']}</th><td>{party.Code}</td>
                </tr>
            </tbody></table>}
            {candidatesForParty.length
                ? <div onKeyDown={onkeyDown}>
                    <QuestionMark>{t['To find the candidate you want to vote for, enter a part of his/her name, slogan or promotional text.']}</QuestionMark>
                    <h2>{t['Candidates']}</h2>
                    {election.ElectionStatus == ElectionStatus.CandidacyPeriod &&
                        <div className="election__CandidacyExplanation">
                            <h4>
                                {t.translate({ NL: 'Kandidaatstelling uitleg:', EN: 'Candidacy explanation.' })}
                            </h4>
                            <ul>
                                <li>
                                    {t.translate({
                                        NL: 'Alle kandidaten moeten zelf inloggen om hun kandidaatstelling goed te keuren! Hiervoor moeten ook de inlognamen ingevuld zijn. Zie voor meer uitleg over aanmaken partij de tekst onder het help-vraagtekentje bovenin.',
                                        EN: 'All candidates must log in themselves to approve their candidacy! The login names must also be entered for this. For more information about creating a party, see the text under the help question mark at the top.'
                                    })}
                                </li>
                            </ul>
                        </div>
                    }
                    <FilterList<CandidateState>
                        searchTerm={searchTermCandidate}
                        onChange={v => dispatch(candidateSearched(v))}
                        label={t['Search candidate']}
                        className="party__candidates"
                        items={candidatesForParty}
                        language={t.languageCode}
                        filter={(candidate, searchTerm) => doesCandidateMatchText(candidate, searchTerm, t)}
                        render={(candidate) => <CandidateItem candidateId={candidate.ClientSideId} key={candidate.ClientSideId} candidateSystem={false} canShowCandidacyApproved={canShowCandidacyApproved } />}
                    />
                </div>
                : <p>{t['No candidates have been registered for this party yet.']}</p>}
            <div className="buttonrow">
                <Link className="linkbutton" to={`/election/${party.ElectionId}`}>{t['Back']}</Link>
                {canEditText
                    ? <Link className="linkbutton" to={`/partyEdit/${party.PartyId}`}>{t['Edit']}</Link>
                    : <span className="linkbutton linkbutton--disabled">{t['Edit']}</span>
                }
                {canEditStructure && <Toggle state={addMode} onChange={(on) => {
                    if (on) {
                        addBlankCandidateAndSetEditMode(party.PartyId, parties, dispatch);
                    }
                    else {
                        removeInlineCandidateIfEmpty(partyScreenState.inlineEditingCandidateId, candidatesForParty, dispatch);
                    }
                    dispatch(addModeSet({ addMode: on }));
                }}>{addMode ? t['Done adding candidates'] : t['Add candidates']}</Toggle>}
            </div>
            {account.IsAdmin && canEditStructure && <SelectPartyAdminScreen electionId={party.ElectionId} partyId={party.PartyId} />}
        </div>
    </ElectionContainer>
}

export const doesCandidateMatchText = (possibleCandidate: CandidateState, searchTerm: string, t: Translator) => {
    if (searchTerm == null) {
        return true;
    }

    searchTerm = searchTerm.toLowerCase();

    const textToBeSearched = t.languageCode === 'NL' ?
        possibleCandidate.Name + possibleCandidate.Slogan_NL + possibleCandidate.ElectionText_NL
        :
        possibleCandidate.Name + possibleCandidate.Slogan_EN + possibleCandidate.ElectionText_EN

    return textToBeSearched.toLowerCase().indexOf(searchTerm) !== -1;
}
